import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import PreviousNumberAnswersContextProvider from '@app/src/context/PreviousNumberAnswersContextProvider'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import type Assessment from '@app/src/types/assessment'
import type { Provider } from '@app/src/types/organizations'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, Solutions } from '@app/src/wf-constants'
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import AssessmentScreenedBanner from '../AllAssessments/AssessmentScreenedBanner'
import AssessmentAnswers from './AssessmentAnswers'
import AssessmentDetailsSidebar from './AssessmentDetailsSidebar'
import AssessmentLearnMoreDialog from './AssessmentLearnMoreDialog'
import AssessmentSummaryCard from './AssessmentSummaryCard'
import RequestDescriptionDialog from './RequestDescriptionDialog'
import ShareDataComingSoonDialog from './ShareDataComingSoonDialog'
import { useAssessmentAnswers } from './useAssessmentAnswers'
import useNavigateToQuestionnaire from './useNavigateToQuestionnaire'

interface AssessmentCompanyPageProps {
  assessment: Assessment
  provider: Provider | undefined
  embedded?: boolean
  maxRowsForLongAnswers?: number
}

const PAGE_MAX_WIDTH = 1200

const AssessmentCompanyPage: React.FC<AssessmentCompanyPageProps> = ({
  assessment,
  provider,
  embedded = false,
  maxRowsForLongAnswers = 3,
}) => {
  const { trackEvent } = useAmplitude()
  const { scope } = useAuthentication()
  const navigateToQuestionnaire = useNavigateToQuestionnaire()
  const [isMethodologyOpen, openMethodology, closeMethodology] = useDialogState(false)
  const [isLearnMoreOpen, openLearnMore, closeLearnMore] = useDialogState(false)
  const [isShareDataOpen, openShareData, closeShareData] = useDialogState(false)

  const isTransparency = scope.solution === Solutions.Transparency
  const isBaseline = assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment
  const { sections, responseItems, primaryResponseItem, isLoading } = useAssessmentAnswers(assessment, provider?.id)

  const trackingProps = { assessmentId: assessment.id, assessmentName: assessment.assessmentTemplate.name }

  useEffect(() => {
    trackEvent({
      name: isBaseline
        ? AmplitudeTrackingEvents.Transparency.Assessment.BaselineAssessmentViewedPage
        : AmplitudeTrackingEvents.Transparency.Assessment.StandardAssessmentViewedPage,
      eventProps: trackingProps,
    })
  }, [assessment.id])

  const handleMethodologyClick = () => {
    trackEvent({
      name: AmplitudeTrackingEvents.Transparency.Assessment.LearnMoreDialogOpened,
      eventProps: trackingProps,
    })
    openMethodology()
  }

  const handleShareDataClick = () => {
    trackEvent({ name: AmplitudeTrackingEvents.Transparency.Assessment.ShareDataClicked, eventProps: trackingProps })
    openShareData()
  }

  if (isLoading) {
    return (
      <Stack flexGrow={1} justifyContent="center" alignItems="center" height="100%" py={8}>
        <CircularProgress />
      </Stack>
    )
  }

  const content = (
    <PreviousNumberAnswersContextProvider responseItems={responseItems}>
      <Stack spacing={2} minWidth={0}>
        <AssessmentSummaryCard
          assessment={assessment}
          provider={provider}
          showSourceChip={!isTransparency}
          showLocationAndRisks={!isTransparency && !isBaseline}
        />
        <AssessmentAnswers
          assessment={assessment}
          sections={sections}
          provider={provider}
          showScoring={!isTransparency}
          maxRowsForLongAnswers={maxRowsForLongAnswers}
        />
      </Stack>
    </PreviousNumberAnswersContextProvider>
  )

  if (embedded) return content

  return (
    <>
      {assessment.isScreened && (
        <AssessmentScreenedBanner
          periodName={assessment.periodName}
          questionnaireTemplateId={assessment.assessmentTemplate.questionnaireTemplateId}
        />
      )}
      <Stack alignItems="center" width="100%" pb={4}>
        <Stack width="100%" maxWidth={PAGE_MAX_WIDTH} px={{ xs: 2, sm: 4 }} spacing={3} mt={4}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h2">{assessment.assessmentTemplate.name}</Typography>
            <Chip label={assessment.periodName} variant="outlined" />
          </Stack>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 1fr) 320px' }}
            gap={3}
            alignItems="start"
          >
            {content}
            <AssessmentDetailsSidebar
              onLearnMoreClick={openLearnMore}
              onMethodologyClick={handleMethodologyClick}
              onViewQuestionnaireClick={() => navigateToQuestionnaire(primaryResponseItem)}
              onShareDataClick={handleShareDataClick}
              isViewQuestionnaireDisabled={!primaryResponseItem}
              hideViewQuestionnaire={assessment.isScreened}
              request={primaryResponseItem?.response?.request}
            />
          </Box>
        </Stack>
      </Stack>
      <AssessmentLearnMoreDialog
        open={isMethodologyOpen}
        onClose={closeMethodology}
        assessmentTemplate={assessment.assessmentTemplate}
      />
      <RequestDescriptionDialog
        open={isLearnMoreOpen}
        onClose={closeLearnMore}
        description={primaryResponseItem?.response?.request?.description}
      />
      <ShareDataComingSoonDialog open={isShareDataOpen} onClose={closeShareData} />
    </>
  )
}

export default AssessmentCompanyPage
